import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  DialogProps,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SxProps, Theme } from "@mui/material/styles";

export interface DialogControlProps {
  open: boolean;
  onClose: () => void;
  /** Dialog title – string oder JSX */
  title: React.ReactNode;
  /** Hauptinhalt des Dialogs */
  children: React.ReactNode;
  /** Optionaler Footer / Actions-Bereich unter dem Content */
  actions?: React.ReactNode;
  /** Optional: zusätzliche Props für den MUI-Dialog */
  dialogProps?: Partial<DialogProps>;
  /** Optional: überschreibt Standard‑Sx der Dialog‑Paper */
  paperSx?: SxProps<Theme>;
  /** Optional: überschreibt Sx des Scroll-Containers im Content */
  contentSx?: SxProps<Theme>;
  /** Optional: überschreibt Sx des Titels (Wrapper-Box) */
  titleSx?: SxProps<Theme>;
  maxWidth?: DialogProps["maxWidth"];
}

/**
 * Wiederverwendbare Dialog-Hülle mit:
 * - zentriertem Titel + Close-Icon rechts
 * - mobile full-screen Layout (100vh / 100vw) mit ohne Rand
 * - scrollbarem Content-Bereich
 * - optionalem Footer / Actions-Bereich
 */
export function DialogControl({
  open,
  onClose,
  title,
  children,
  actions,
  dialogProps,
  paperSx,
  contentSx,
  titleSx,
  maxWidth,
}: DialogControlProps) {
  const theme = useTheme();
  // xs + sm: fullscreen
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const handleCloseInternal = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseInternal}
      fullScreen={isSmDown}
      fullWidth={!isSmDown}
      maxWidth={isSmDown ? false : maxWidth || "sm"}
      aria-labelledby="app-dialog-title"
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column", // <<< wichtig
          ...(isSmDown
            ? {
                borderRadius: 0,
                border: "none",
                m: 0,
                width: "100vw",
                height: "100vh",
                maxWidth: "100vw",
                maxHeight: "100vh",
              }
            : {
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                maxHeight: "90vh",
                minWidth: 360,
              }),
          ...paperSx,
        },
      }}
      {...dialogProps}
    >
      <DialogTitle
        id="app-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: isSmDown ? 2 : 3,
          py: isSmDown ? 1.5 : 2,
          ...titleSx,
        }}
      >
        <Box sx={{ width: 40 }} />
        {title}
        <IconButton onClick={handleCloseInternal}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          // nimmt den ganzen Platz zwischen Title und Actions ein
          flex: 1,
          display: "flex",
          flexDirection: "column",
          m: isSmDown ? 0 : 2,
          p: isSmDown ? 0 : 0,
        }}
      >
        {/* Scrollbarer Content-Bereich, wächst mit */}
        <Box
          sx={{
            flex: 1, // <<< lässt den Content wachsen
            overflowY: "auto",
            px: isSmDown ? 2 : 1,
            pt: isSmDown ? 2 : 0,
            ...contentSx,
          }}
        >
          {children}
        </Box>

        {/* Actions sind immer unten, außerhalb des Scrollbereichs */}
        {actions && (
          <Box
            sx={{
              p: isSmDown ? 2 : 3,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {actions}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
